/**
 * TTS Playback
 *
 * Shared AudioContext + singleton speak/cancel for the chat panel.
 * Only one utterance plays at a time; speaking a new message cancels
 * the current one. Subscribers observe which message is active so the
 * per-message speaker button can reflect state.
 */

import { synthesizeSpeech } from './synthesize-speech';

const AUTOPLAY_STORAGE_KEY = 'ai.tts.autoplay';

export type TtsState = 'idle' | 'loading' | 'playing';

interface TtsStatus {
    activeId: string | null;
    state: TtsState;
}

let audioContext: AudioContext | null = null;
let currentAbort: AbortController | null = null;
let status: TtsStatus = { activeId: null, state: 'idle' };
const listeners = new Set<(s: TtsStatus) => void>();

function setStatus(next: TtsStatus) {
    status = next;
    for (const l of listeners) l(status);
}

function getAudioContext(): AudioContext {
    if (!audioContext) {
        const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) throw new Error('AudioContext not supported');
        audioContext = new Ctor();
    }
    if (audioContext.state === 'suspended') {
        void audioContext.resume();
    }
    return audioContext;
}

export function getTtsStatus(): TtsStatus {
    return status;
}

export function subscribeTts(fn: (s: TtsStatus) => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

export function cancelTts(): void {
    if (currentAbort) {
        currentAbort.abort();
        currentAbort = null;
    }
    setStatus({ activeId: null, state: 'idle' });
}

/**
 * Speak text for the given message id. If that id is already active,
 * cancels playback. Any other active utterance is cancelled first.
 */
export async function speakMessage(id: string, text: string): Promise<void> {
    if (status.activeId === id) {
        cancelTts();
        return;
    }
    if (currentAbort) {
        currentAbort.abort();
        currentAbort = null;
    }

    const ctx = getAudioContext();
    const abort = new AbortController();
    currentAbort = abort;
    setStatus({ activeId: id, state: 'loading' });

    try {
        await synthesizeSpeech(
            text,
            ctx,
            abort.signal,
            undefined,
            () => {
                if (currentAbort === abort) {
                    setStatus({ activeId: id, state: 'playing' });
                }
            },
        );
    } catch (err) {
        if (!abort.signal.aborted) {
            console.error('[TTS] playback failed', err);
        }
    } finally {
        if (currentAbort === abort) {
            currentAbort = null;
            setStatus({ activeId: null, state: 'idle' });
        }
    }
}

export function getAutoplayEnabled(): boolean {
    return localStorage.getItem(AUTOPLAY_STORAGE_KEY) === '1';
}

export function setAutoplayEnabled(enabled: boolean): void {
    if (enabled) localStorage.setItem(AUTOPLAY_STORAGE_KEY, '1');
    else localStorage.removeItem(AUTOPLAY_STORAGE_KEY);
}
